import type { ReactNode } from 'react'
import { Children, useContext } from 'react'

import { createSharedContext } from '@twistopayments/design-system/utils'

import { StepIndexProvider } from './step-index.context'
import type { StepperVariant } from './stepper'

/**
 * Context
 */

type ContextValue = {
  backgroundColor: string
  connectorColor?: string
  iconRingColor?: string
  inverse: boolean
  stepsCount: number
  variant: StepperVariant
}

const Context = createSharedContext<ContextValue | undefined>('components/molecules/stepper/stepper/stepper-config.context#0', undefined)

/**
 * Provider
 */

type Props = {
  backgroundColor: string
  children: ReactNode
  connectorColor?: string
  iconRingColor?: string
  inverse?: boolean
  variant: StepperVariant
}

export const StepperConfigProvider = ({
  backgroundColor,
  children,
  connectorColor,
  iconRingColor,
  inverse = false,
  variant,
}: Props) => {
  const stepsCount = Children.count(children)

  return (
    <Context.Provider
      value={{
        backgroundColor,
        connectorColor,
        iconRingColor,
        inverse,
        stepsCount,
        variant,
      }}
    >
      {Children.map(children, (child, index) => (
        <StepIndexProvider index={index + 1}>{child}</StepIndexProvider>
      ))}
    </Context.Provider>
  )
}

/**
 * Hook
 */

export const useStepperConfig = () => {
  const context = useContext(Context)

  if (context === undefined) {
    throw new Error(
      'useStepperConfig must be used within a StepperConfigProvider'
    )
  }

  return context
}
